import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { OrganizationLinkEditModal } from "@/pages/settings/organization-links/widgets/OrganizationLinkEditModal.tsx";
import {
  useOrganizationLinkDelete,
  useOrganizationLinks,
  useOrganizationLinksUpdateSort,
} from "@/queries/use-organization-links.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiOrganizationLink } from "@/types/organization-link.types.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import {
  Box,
  Button,
  ButtonIcon,
  ConfirmationModal,
  FontAwesomeIcon,
  LinkTo,
  ListItem,
  Page,
  Skeleton,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export function OrganizationLinks() {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { truncate } = useFormatter();
  useTitle(t("core:organization_links"), t("common:settings"));

  const { data: links, isLoading } = useOrganizationLinks();
  const organizationLinkDelete = useOrganizationLinkDelete();
  const organizationLinksUpdateSort = useOrganizationLinksUpdateSort();

  const [localLinks, setLocalLinks] = useState<ApiOrganizationLink[] | null>(null);
  const displayLinks = localLinks ?? links ?? [];

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.organizationLinks.viewed);
  });

  const handleCreate = useCallback(() => {
    openModal(OrganizationLinkEditModal, { organizationLink: null });
  }, []);
  const handleEdit = useCallback((link: ApiOrganizationLink) => {
    openModal(OrganizationLinkEditModal, { organizationLink: link });
  }, []);
  const handleDelete = useCallback((link: ApiOrganizationLink) => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await organizationLinkDelete.mutateAsync(link.id);
          analyticsService.trackEvent(analyticEvents.organizationLinks.deleted, {
            [analyticProperties.id]: link.id,
          });
        } catch {}
      },
    });
  }, []);

  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      if (!result.destination) return;
      if (result.destination.index === result.source.index) return;

      const current = localLinks ?? links ?? [];
      const reordered = [...current];
      const [moved] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, moved);

      setLocalLinks(reordered);

      try {
        await organizationLinksUpdateSort.mutateAsync(reordered.map((l) => l.id));
        setLocalLinks(null);
      } catch {
        setLocalLinks(null);
      }
    },
    [localLinks, links]
  );

  return (
    <Page>
      <Page.Header showBack={true} title={t("core:organization_links")} navigateTo={"/settings"} />
      <Page.Content className="mx-auto w-full max-w-4xl">
        <Stack gap="sm">
          <Stack direction="horizontal" justify="between" items="center">
            <Title header={t("core:links")} size="xs" />
            <Button
              type="button"
              variant="secondary"
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
              onClick={handleCreate}
            >
              {t("core:add_link")}
            </Button>
          </Stack>
          {isLoading ? (
            <>
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-14 w-full" />
              ))}
            </>
          ) : displayLinks.length === 0 ? (
            <Text className="text-secondary">{t("common:no_records_yet")}</Text>
          ) : (
            <>
              <Text className="text-secondary">{t("common:drag_to_reorder")}</Text>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="organization-links">
                  {(provided) => (
                    <div
                      className="flex flex-col"
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {displayLinks.map((link, index) => (
                        <Draggable
                          key={`link-${link.id}`}
                          draggableId={`link-${link.id}`}
                          index={index}
                        >
                          {(provided) => (
                            <div
                              className="my-1 rounded-xs border border-dark/5 bg-dark/5 px-4 py-2"
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                            >
                              <Box>
                                <Stack direction="horizontal" justify="between" items="center">
                                  <Stack>
                                    <ListItem
                                      title={link.title}
                                      behavior={"card"}
                                      captionSlot={
                                        <LinkTo
                                          to={link.url}
                                          variant={"secondary"}
                                          target={"_blank"}
                                          className="text-secondary text-sm"
                                        >
                                          {truncate(link.url, 80)}
                                        </LinkTo>
                                      }
                                    />
                                  </Stack>
                                  <Stack direction="horizontal" gap="sm" items="center">
                                    <ButtonIcon
                                      variant="plain"
                                      icon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                                      onClick={() => handleDelete(link)}
                                    />
                                    <ButtonIcon
                                      variant="plain"
                                      icon={<FontAwesomeIcon icon="fa-light fa-pen-to-square" />}
                                      onClick={() => handleEdit(link)}
                                    />
                                    <Box {...provided.dragHandleProps}>
                                      <FontAwesomeIcon
                                        className="m-2"
                                        icon="fa-light fa-grip-dots-vertical"
                                      />
                                    </Box>
                                  </Stack>
                                </Stack>
                              </Box>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          )}
        </Stack>
      </Page.Content>
    </Page>
  );
}
